import React, { useEffect, useReducer, useRef } from 'react';
import NumberInput from '../../../../components/bearing-search/components/forms/NumberInput';
import { convertNumberInput } from '../../../utils/validations';
import { temperatureConversionReducer, temperatureConversionInitialState, TemperatureConversionState } from './reducer';
import { useConversion } from '../../ConversionContext';
import { getConvertedUnit } from '../../../../components/bearing-search/api/api';
import { ConversionToolTabProps } from '../../types';
import { ConversionUnitsPayload } from '../../../../components/bearing-search/types/types';
import { mapConversionAnalyicsData } from '../../Utils/AnalyticsUtils';
import { tabInputDebounceFunction } from '../Utils/TabUtils';
import { trackScientificNotationChangeEvent } from "../../../utils/analytics";

const TabTemperature: React.FC<ConversionToolTabProps> = ({ description }) => {
	const { exponent, precision } = useConversion();

	const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
	const lastInputRef = useRef<{
		category: 'temperature' | 'pressure';
		unit: string;
		value: string;
	} | null>(null);

	const lastResetCategoryRef = useRef<'temperature' | 'pressure' | null>(null);
	// Reducer
	const [ temperatureConversionState, dispatch ] = useReducer(
		temperatureConversionReducer,
		temperatureConversionInitialState,
	);
	const currentStateRef = useRef();

	/**
	 * Recalculate all conversions units
	 */
	const recalculateAllConversions = async () => {
		let changedCategories = [];

		for (const [ categoryKey, units] of Object.entries(temperatureConversionState)) {
			const category = categoryKey as keyof TemperatureConversionState;

			for (const [ unit, value] of Object.entries(units)) {
				if (value !== undefined && value !== '' && value !== '0') {
					if (!changedCategories.includes(category)){
						changedCategories.push(category);
					}
					if (!changedCategories.includes('precision exponent') && exponent) {
						changedCategories.push('precision exponent');
					}
					if (!changedCategories.includes('precision') && precision != 4) {
						changedCategories.push('precision');
					}
					callAndDispatchConversion(category, unit, value, true);
				}
			}
		}

		return changedCategories;
	};

	/**
	 * Reset all conversions units
	 */
	const resetAllConversions = () => {
		for (const [ categoryKey, units] of Object.entries(temperatureConversionState)) {
			const category = categoryKey as keyof TemperatureConversionState;

			for (const [ value ] of Object.entries(units)) {
				if (value !== undefined && value !== '' && value !== '0') {
					dispatch({
						type: 'reset_category',
						payload: {
							category,
						},
					});
				}
			}
		}
	}

	/**
	 * Update the converted values with the new exponent
	 * convert based on the first input of each category
	 */
	useEffect(() => {
		currentStateRef.current = temperatureConversionState;
		recalculateAllConversions().then((result) => {
			trackScientificNotationChangeEvent('conversionTool', result);
		});
	}, [ exponent ]);

	/**
	 * Reset the values when precision is changed
	 */
	useEffect(() => {
		resetAllConversions();
	}, [ precision ]);

	// Map of conversion types for API payload
	const conversionTypeMap: Record<string, string[]> = {
		c: [ 'celsiusToFahrenheit' ],
		f: [ 'fahrenheitToCelsius' ],
		pa: [ 'pascalToPsi', 'pascalToMegapascal', 'pascalToksi' ],
		psi: [ 'psiTopascal', 'psiToMegapascal', 'psiToksi' ],
		mpa: [ 'megapascalToPascal', 'megapascalTopsi', 'megapascalToksi' ],
		ksi: [ 'ksitopascal', 'ksiTopsi', 'ksiTomegapascal' ],
		// lbf: ["poundForceToNewton", "poundForceToKilonewton"]
	};

	// Map of converted units from API response
	const convertedUnitMap: Record<string, string> = {
		celsiusToFahrenheit: 'f',
		fahrenheitToCelsius: 'c',
		pascalToPsi: 'psi',
		pascalToMegapascal: 'mpa',
		pascalToksi: 'ksi',
		psiTopascal: 'pa',
		psiToMegapascal: 'mpa',
		psiToksi: 'ksi',
		megapascalToPascal: 'pa',
		megapascalTopsi: 'psi',
		megapascalToksi: 'ksi',
		ksitopascal: 'pa',
		ksiTopsi: 'psi',
		ksiTomegapascal: 'mpa',
	};

	/**
	 * @param  category
	 * @param  unit
	 * @param  value
	 * @param skipAnalyticCall
	 */
	const callAndDispatchConversion = async (
		category: 'temperature' | 'pressure',
		unit: string,
		value: string,
		skipAnalyticCall: boolean
	) => {
		const conversionTypeArray = conversionTypeMap[ unit ] || [];
		if (conversionTypeArray.length === 0) {
			return;
		}

		const conversionPayload: ConversionUnitsPayload = {
			unitConversionInputs: [
				{
					conversionType: conversionTypeArray,
					inputValue: value,
					precision,
					exponent,
				},
			],
		};

		try {
			const response = await getConvertedUnit(conversionPayload);

			// ANALYTICS
			if (!skipAnalyticCall) {
				mapConversionAnalyicsData(category, conversionPayload, response);
			}

			if (response.unitConversionResults && category !== lastResetCategoryRef.current) {
				for (const conversionResult of response.unitConversionResults) {
					const convertedType = conversionResult.convertedTypes;
					const convertedValue = conversionResult.convertedValue;
					const convertedUnit = convertedUnitMap[ convertedType ];

					if (! convertedUnit) {
						console.log(`${ convertedType } index doesn't exit in convertedUnitMap`);
						return;
					}

					const converted = {
						[ convertedUnit ]: convertedValue.replaceAll(/,/g, ''),
					};

					dispatch({
						type: 'update_unit',
						payload: {
							category,
							updatedUnits: converted,
						},
					});
				}
			} else {
				dispatch({
					type: 'reset_category',
					payload: {
						category,
					},
				});
			}
		} catch (error) {
			console.error('Conversion API failed', error);
		}
	};

	/**
	 * @param  category
	 * @param  unit
	 * @param  value
	 */
	const handleInputChange = (
		category: 'temperature' | 'pressure',
		unit: string,
		value: string,
	) => {
		const numericValue = convertNumberInput(value);

		if (numericValue === undefined) {
			dispatch({
				type: 'reset_category',
				payload: { category },
			});

			lastResetCategoryRef.current = category;

			return; // return, don't call API
		}

		dispatch({
			type: 'update_unit',
			payload: {
				category,
				updatedUnits: { [ unit ]: value },
			},
		});

		lastResetCategoryRef.current = null;
		lastInputRef.current = { category, unit, value };

		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
		}
		const debounceDelay = value.length < 2 ? 0 : 600 + (value.length * 100);
		debounceTimeout.current = setTimeout(() => {
			tabInputDebounceFunction(lastInputRef, callAndDispatchConversion);
		}, debounceDelay);
	};

	/**
	 * @param currentValue
	 * @param category
	 * @param unit
	 * @param newValue
	 */
	const handleBlur = (
		currentValue: string,
		category: 'temperature' | 'pressure',
		unit: string,
		newValue: string,
	) => {
		if (debounceTimeout.current) {
			clearTimeout(debounceTimeout.current);
			debounceTimeout.current = null;
		}

		const numericValue = Number.parseFloat(newValue);
		if (Number.isNaN(numericValue) || numericValue === 0) {
			return;
		}

		if (currentValue == newValue) {
			return;
		}

		callAndDispatchConversion(category, unit, newValue, false);
	};

	return (
		<>
			{/* Temperature */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Temperature</span>
				</div>
				<div className="col-12 col-md-8">
					<div className="row align-items-center mx-md-0">
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-temperature-c"
								label={''}
								unitLabel={'°C'}
								value={temperatureConversionState.temperature.c}
								min={0}
								onChange={(e) => handleInputChange('temperature', 'c', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'temperature', 'c', temperatureConversionState.temperature.c ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-temperature-f"
								label={''}
								unitLabel={'°F'}
								value={temperatureConversionState.temperature.f}
								min={0}
								onChange={(e) => handleInputChange('temperature', 'f', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'temperature', 'f', temperatureConversionState.temperature.f ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Pressure */}
			<div className='row align-items-center g-0 mb-4'>
				<div className="col-12 col-md-4 col-conversion-category-label">
					<span>Pressure</span>
				</div>
				<div className="col-12 col-md-8">
					<div className="row align-items-center mx-md-0">
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-pressure-pa"
								label={''}
								unitLabel={'Pa'}
								value={temperatureConversionState.pressure.pa}
								min={0}
								onChange={(e) => handleInputChange('pressure', 'pa', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'pressure', 'pa', temperatureConversionState.pressure.pa ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-pressure-psi"
								label={''}
								unitLabel={'Psi'}
								value={temperatureConversionState.pressure.psi}
								min={0}
								onChange={(e) => handleInputChange('pressure', 'psi', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'pressure', 'psi', temperatureConversionState.pressure.psi ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
					<div className="row align-items-center mx-md-0">
						<div className="col-12 col-md-4 px-md-0">
							<NumberInput
								id="conversion-pressure-mpa"
								label={''}
								unitLabel={'MPa'}
								value={temperatureConversionState.pressure.mpa}
								min={0}
								onChange={(e) => handleInputChange('pressure', 'mpa', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'pressure', 'mpa', temperatureConversionState.pressure.mpa ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
						<div className="col-12 col-md-4 pe-md-0">
							<NumberInput
								id="conversion-pressure-ksi"
								label={''}
								unitLabel={'ksi'}
								value={temperatureConversionState.pressure.ksi}
								min={0}
								onChange={(e) => handleInputChange('pressure', 'ksi', e.target.value)}
								onBlur={(e) => handleBlur(e.target.value, 'pressure', 'ksi', temperatureConversionState.pressure.ksi ?? '')}
								onFocus={(e) => e.target.select()}
							/>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default TabTemperature;
